<script setup>
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted, watch, inject } from 'vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import learningApiUser from '@src/services/api/learning/user'
import learningApiCourses from '@src/services/api/learning/course'
import learningApiMasters from '@src/services/api/learning/masters'
import { useLearningAuthStore } from '@stores/auth'
import { useLearningUserStore } from '@stores/user'
import Modal from '@components/modal/Modal.vue'
import TextbookDownload from '@components/course/TextbookDownload.vue'
import DVDorMovie from '@components/course/DVDorMovie.vue'
import { commonUtil } from '@utils'
import { downloadTraineePdf, isSessionExpiredPdfError } from '@utils/pdfDownload'
import { toast } from 'vue3-toastify'

const router = useRouter()
const rootAppProvide = inject('rootAppProvide')
const { accessToken } = storeToRefs(useLearningAuthStore())
const { alreadyEntry, trainable } = storeToRefs(useLearningUserStore())
const { setAlreadyEntry, setUserInfo } = useLearningUserStore()
const { resetAuth } = useLearningAuthStore()

const keywords = ref([])
const questionnaire = ref([])
const paymentMethod = ref({})
const userModifiable = ref(true)

// 全講座リスト
const courseList = ref([])

// 申込み済講座
const detail = ref(null)

// オンラン講座リスト
const onlineList = computed (() => {
  return courseList.value.filter((item) => {
    return item.type === 'online'
  })
})

// オンデマンド講座視聴方法（dvd or online）
const onlineMethod = ref(null)

// 会場講座リスト
const realList = computed (() => {
  return courseList.value.filter((item) => {
    return item.type === 'real'
  })
})

// モーダル設定
const showPaySelectModal = ref(false)
const showTraineeCancelModal = ref(false)
const showOnlineCompleteModal = ref(false)
const showQuestionnaireCompleteModal = ref(false)
const showCancelCompleteModal = ref(false)
const showConfirmPDFModal = ref(false)
const showWithdrawalModal = ref(false)
const showOnlineStartModal = ref(false)
const tempOnlineMethod = ref(null)
const agree = ref(false)

// エラー関連
const errorOnlineKeyword = ref('')

const traineeStatus = ref({
  paymentSelect: null, // 支払い方法選択済か。選択済ならSEVENTEEN_ELEVENなど
  paymentStatus: null, // 支払い状況。-1:未払い, 1:支払済, -2:返金済
  paymentKonbiniCode: '' // コンビニ支払いの場合のコンビニコード
})

// 申込講座詳細取得
const getTrainee = () => {
  rootAppProvide.value.openContentsLoading()
  setAlreadyEntry(false)

  // 申込み済講座取得
  const traineeParams = {
    params: {
      with: 'course.location',
      course_year: dayjs().year()
    }
  }
  learningApiUser.getTrainee(traineeParams).then((res) => {
    console.log('getTraniee res', res.data)
    if (res.data.length === 0) return

    // TODO 1つしか確実にない？？
    detail.value = res.data.find((item) => {
      return item.enabled && item.course
    })

    if (detail.value === undefined) {
      return false
    }
    setAlreadyEntry(true)

    console.log('detail.value', detail.value)
    getDetail(detail.value.id)
  }).catch((err) => {
    console.error('login err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

// 取得した予約済み講座の詳細を取得
const getDetail = (traineeId) => {
  rootAppProvide.value.openContentsLoading()
  const getDetailParams = {
    params: {
      with: 'course.location|payments_en'
    },
    custom: {
      params: { traineeId: traineeId }
    }
  }
  // 講座詳細と講座支払い詳細を取得
  Promise.all([
    learningApiUser.getTraineeDetail(getDetailParams)
  ]).then((res) => {
    console.log('res', res)
    if (!res[0].data.course) {
      return false
    }
    detail.value = res[0].data
    if (res[0].data.course.questionnaire?.length > 0) {
      questionnaire.value = res[0].data.course.questionnaire.map(() => '')
    }
    traineeStatus.value.paymentStatus = detail.value.payment_status
    console.log('detail.value.payments_en.lenght', detail.value.payments_en[0])
    if (detail.value.payments_en[0] === undefined) {
      return false
    }
    traineeStatus.value.paymentSelect = detail.value.payments_en[0].payment_by
    traineeStatus.value.paymentKonbiniCode = detail.value.payments_en[0].gmopay?.konbiniCode
    console.log('traineeStatus.value', traineeStatus.value)

    // オンデマンドで受講中（開始あり、終了なし）であればキーワードを取得
    if (detail.value.course.type === 'online' && (detail.value.start_at && !detail.value.end_at)) {
      const customParams = {
        custom: {
          params: {
            traineeId: detail.value.id
          }
        }
      }
      learningApiUser.postTraineeOnlineStart({}, customParams).then((res) => {
        keywords.value = []
        res.data.keywords.forEach((item) => {
          console.log('keyword', item)
          keywords.value.push({
            title: item.title,
            keyword: ''
          })
        })
        // console.log('keywords.value', keywords.value, res.data.urls)
      }).catch((err) => {
        console.error('keywords err', err)
      })
    }
  }).catch((err) => {
    console.error('getTraineeDetail err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

// トップメッセージテキスト生成
const topMessage = computed(() => {
  const year = dayjs().year()
  // 1．講座未登録
  if (courseList.value.length === 0) {
    return `${year}年度継続的研修の受講申込受付開始までお待ちください。`

  // 申込済み講座あり
  } else if (detail.value) {
    return commonUtil.getTraineeStatus(detail.value).message
  
  // 申込済み講座なし
  } else {
    // 2．講座登録（申込期間前）※複数ある場合は、リストから一番早い日付を表示
    // console.log('申込み済なし', courseList.value)
    if (courseList.value.every((item) => {
      return new Date(item.entryterm[0]) > new Date()
    })) {
      const earliestStartDate = courseList.value.reduce((earliest, current) => {
        const startDate = new Date(current.entryterm[0])
        if (!earliest || startDate < earliest) {
          return startDate
        }
        return earliest
      }, null)
      return `${year}年度継続的研修の受講申込受付は${dayjs(earliestStartDate).format('YYYY年MM月DD日(dd)')}より開始予定です。`;

    // 3．講座受付開始/未受付（申込期間中）※複数ある場合は、期間中でリストから一番遅い日付を表示
    } else if (courseList.value.some((item) => {
      return new Date(item.entryterm[0]) <= new Date() && new Date(item.entryterm[1]) >= new Date()
    })) {
      const latestEndDate = courseList.value.reduce((latest, current) => {
        const startDate = new Date(current.entryterm[0])
        const endDate = new Date(current.entryterm[1])
        if (startDate <= new Date() && endDate >= new Date() && (!latest || endDate > latest)) {
          return endDate
        }
        return latest
      }, null)
      return `${year}年度継続的研修の受講申込受付は${dayjs(latestEndDate).format('YYYY年MM月DD日(dd)')}までです。`
    
    // 6．講座受付終了/未受付（申込期間後）
    } else if (courseList.value.every((item) => {
      return new Date(item.entryterm[1]) < new Date()
    
    // 11．受講期間終了/未受講、12．受講期間終了/受講開始
    })) {
      return `${year}年度継続的研修の受講申込は終了しました。`
    }
  }
})

/*
 * 支払い関連
 */

const tempPaySelect = ref({
  payment_by: '',
  konbiniCode: '',
  name: ''
})

// 支払い確認モーダル
const openPaySelectModal = (payment_by, konbiniCode) => {
  console.log('openPaySelectModal', payment_by, konbiniCode)
  if (payment_by === 'GMOPay/KONBINI') {
    if (konbiniCode === 'SEVEN_ELEVEN') {
      tempPaySelect.value.name = 'コンビニ払い/セブンイレブン'
    } else if (konbiniCode === 'LAWSON') {
      tempPaySelect.value.name = 'コンビニ払い/ローソン'
    } else if (konbiniCode === 'FAMILYMART') {
      tempPaySelect.value.name = 'コンビニ払い/ファミリーマート'
    } else if (konbiniCode === 'MINISTOP') {
      tempPaySelect.value.name = 'コンビニ払い/ミニストップ'
    } else if (konbiniCode === 'SEICOMART') {
      tempPaySelect.value.name = 'コンビニ払い/セイコーマート'
    }
  } else if (payment_by === 'GMOPay/BANK_TRANSFER_GMO_AOZORA') {
    tempPaySelect.value.name = '銀行振り込み'
  } else if (payment_by === 'GMOPay/PAYEASY') {
    tempPaySelect.value.name = 'ペイジー払い'
  }
  tempPaySelect.value.payment_by = payment_by
  tempPaySelect.value.konbiniCode = konbiniCode
  showPaySelectModal.value = true
}

const toPayDetail = () => {
  router.push({
    name: 'TraineePay',
    params: {
      courseId: detail.value.id
    },
    query: {
      payment_by: traineeStatus.value.paymentSelect,
      konbiniCode: traineeStatus.value.paymentKonbiniCode
    }
  })
}

// 支払い方法の可否
const isPayable = (name) => {
  try {
    const minimamu = paymentMethod.value.user[name].minimamu
    const maximum = paymentMethod.value.user[name].maximum
    // nullの場合は制限なし
    if (minimamu === null && maximum === null) {
      return true
    } else if (minimamu === null) {
      return paymentMethod.value.user[name].maximum >= detail.value.course.price
    } else if (maximum === null) {
      return paymentMethod.value.user[name].minimamu <= detail.value.course.price
    } else {
      return (paymentMethod.value.user[name].minimamu <= detail.value.course.price) && (paymentMethod.value.user[name].maximum >= detail.value.course.price)
    }
  } catch (e) {
    return false
  }
}

// 支払い方法を送信して詳細画面へ
const toPay = () => {
  rootAppProvide.value.openContentsLoading()
  const params = {
    payment_by: tempPaySelect.value.payment_by,
    konbiniCode: tempPaySelect.value.konbiniCode
  }
  const customParams = {
    custom: {
      params: {
        traineeId: detail.value.id
      }
    }
  }
  // 支払い方法を送信
  learningApiUser.postTraineePay(params, customParams).then((res) => {
    console.log('postTraineePay res', res.data)
    router.push({
      name: 'TraineePay',
      params: {
        courseId: detail.value.id
      },
      query: {
        payment_by: tempPaySelect.value.payment_by,
        konbiniCode: tempPaySelect.value.konbiniCode
      }
    })
  }).catch(error => {
    console.error('postTraineePay error', error)
    if (error.response) {
      if (error.response?.data?.error === "user.displays.spell:missmatch") {
        toast('エラーが発生しました。名前（カナ）を確認してください。', {
          'type': 'error'
        })
      } else {
        toast('エラーが発生しました。(error: ' + error.response?.data?.error + ')', {
          'type': 'error'
        })
      }
    } else if (error.message === 'Cannot read properties of undefined (reading \'value\')') {
      console.log('error', error.message)
      toast('エラーが発生しました。\n登録氏名において旧字、環境依存文字はご利用できません。お手数ですが、登録ご氏名を常用漢字にて変更お願いします。', {
        'type': 'error'
      })
    } else {
      toast('エラーが発生しました。(error: ' + error.message + ')', {
        'type': 'error'
      })
    }
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
    showPaySelectModal.value = false
  })
}

// オンデマンド講座開始（キーワードもここで取得）
const startOnline = (method) => {
  tempOnlineMethod.value = method
  showOnlineStartModal.value = true
}

// 追加：オンデマンド開始確認後の処理
const confirmOnlineStart = () => {
  if (!agree.value) {
    toast('同意確認事項の一番下のチェックボックスをONにして同意してください（スマートフォンの場合はスクロールしてください）', {
      type: 'error',
      autoClose: 3000
    })
    return
  }
  onlineMethod.value = tempOnlineMethod.value
  showOnlineStartModal.value = false
  rootAppProvide.value.openContentsLoading()
  const customParams = {
    custom: {
      params: {
        traineeId: detail.value.id
      }
    }
  }
  learningApiUser.postTraineeOnlineStart({}, customParams).then((res) => {
    console.log('postTraineeOnlineStart res', res)
    keywords.value = []
    res.data.keywords.forEach((item) => {
      console.log('keyword', item)
      keywords.value.push({
        title: item.title,
        keyword: ''
      })
    })
    console.log('keywords.value', keywords.value, res.data.urls)
  }).catch((err) => {
    console.error('keywords err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

// オンデマンド講座終了（キーワードの送信）
const finishOnline = () => {
  console.log('finishOnline')
  rootAppProvide.value.openContentsLoading()
  const params = {
    keywords: keywords.value.map((item) => item.keyword),
    attend_by: onlineMethod.value
  }
  const customParams = {
    custom: {
      params: {
        traineeId: detail.value.id
      }
    }
  }
  learningApiUser.postTraineeOnlineEnd(params, customParams).then((res) => {
    console.log('postTraineeOnlineEnd res', res)
    showOnlineCompleteModal.value = true
  }).catch((err) => {
    console.error('postTraineeOnlineEnd err', err)
    if (err.response.status === 422 || err.response.data.error === "keywordMissmatch") {
      errorOnlineKeyword.value = 'キーワードに誤りがあります。もう一度ご確認ください。'
    } else {
      errorOnlineKeyword.value = 'エラーが発生しました。もう一度お試しください。'
    }
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

// 完了モーダルを閉じて再描画
const hideOnlineCompleteModal = () => {
  showOnlineCompleteModal.value = false
  getTrainee()
}

/*
 * PDF関連
 */

const targetPDF = ref('')
const isPdfDownloading = ref(false)

// PDF確認モーダルを開く
const confirmPDFDL = (type) => {
  showConfirmPDFModal.value = true
  targetPDF.value = type
}

// PDF確認モーダルを閉じて初期化
const cancelPDFDL = () => {
  showConfirmPDFModal.value = false
  targetPDF.value = null
}

const PDFModalAgree = () => {
  if (isPdfDownloading.value || !targetPDF.value) return

  showConfirmPDFModal.value = false
  isPdfDownloading.value = true
  rootAppProvide.value.openContentsLoading()

  downloadTraineePdf(targetPDF.value, detail.value.id)
    .catch((err) => {
      if (!isSessionExpiredPdfError(err)) {
        console.error('PDF download err', err)
      }
    })
    .finally(() => {
      isPdfDownloading.value = false
      targetPDF.value = null
      rootAppProvide.value.closeContentsLoading()
    })
}

// 受講票ページを開く
const openTraineeCard = () => {
  const params = { courseId: detail.value.id }
  const routeData = router.resolve({ name: 'TraineeCard', params })
  window.open(routeData.href, '_blank')
}

// アンケートのチェックボックスのみここで処理
const updateQuestionnaire = (index, answer) => {
  // console.log('updateQuestionnaire', index, answer, questionnaire.value[index])
  if (questionnaire.value[index] === '') {
    questionnaire.value[index] = []
  }
  // すでに登録されている場合は削除、登録されていなければ追加
  if (questionnaire.value[index].includes(answer)) {
    let i = questionnaire.value[index].indexOf(answer)
    questionnaire.value[index].splice(i, 1)
  } else {
    questionnaire.value[index].push(answer)
  }
}

// アンケートの送信可否判定
const isPostableQuestionnaire = computed (() => {
  let flag = true
  questionnaire.value.forEach((item, index) => {
    // アンケートタイプがフリーテキスト以外で、回答が空、もしくはチェックボックスで配列が空の場合は送信不可
    const type = detail.value.course.questionnaire[index].type
    console.log('type', type, item)
    if (type !== 'text') {
      if (type === 'checkbox' && item.length === 0) {
        console.log('checkbox empty')
        flag = false
      } else if (item === '') {
        flag = false
      }
    }
  })
  return flag
})

// アンケートのフリーテキストで入力文字数上限を超えた場合にエラー表示
const checkLengthQuestionnaireTextArea = (index) => {
  // console.log('checkLengthQuestionnaireTextArea', questionnaire.value[index], questionnaire.value[index].length)
  if (questionnaire.value[index].length > 200) {
    toast('200文字以内で入力してください。', {
      'type': 'error',
      'autoClose': 3000
    })
    questionnaire.value[index] = questionnaire.value[index].substr(0, 200)
  }
}

// アンケート回答送信
const postQuestionnaire = () => {
  console.log('postQuestionnaire', questionnaire.value)
  rootAppProvide.value.openContentsLoading()
  // sendAnswersにディープコピー
  const sendAnswers = JSON.parse(JSON.stringify(questionnaire.value)) 
  sendAnswers.forEach((item, index) => {
    if (item === '') {
      sendAnswers[index] = null
    }
  })
  console.log('postQuestionnaire sendAnswers', sendAnswers)
  const params = {
    questionnaire: sendAnswers
  }
  const customParams = {
    custom: {
      params: {
        traineeId: detail.value.id
      }
    }
  }
  learningApiUser.postTraineeQuestionnaire(params, customParams).then((res) => {
    console.log('postTraineeQuestionnaire res', res)
    showQuestionnaireCompleteModal.value = true
  }).catch((err) => {
    console.error('postTraineeQuestionnaire err', err)
  }).finally(() => {
    showQuestionnaireCompleteModal.value = false
    rootAppProvide.value.closeContentsLoading()
    getTrainee()
  })
}

// 申込みキャンセル
const traineeCancel = () => {
  rootAppProvide.value.openContentsLoading()
  showTraineeCancelModal.value = false
  const customParams = {
    custom: {
      params: {
        traineeId: detail.value.id
      }
    }
  }
  learningApiUser.deleteTraineeCancel(customParams).then((res) => {
    console.log('deleteTraineeCancel res', res)
    showCancelCompleteModal.value = true
    getTrainee()
    // 申込みキャンセル後に全講座一覧を再取得
    getCourses()
  }).catch((err) => {
    console.error('deleteTraineeCancel err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

// PDFダウンロード期間内かどうかを判定
const isWithinTextbookTerm = computed(() => {
  if (!detail.value?.course?.displays?.textbookterm) return false
  
  const now = dayjs()
  const startDate = dayjs(detail.value.course.displays.textbookterm[0])
  const endDate = dayjs(detail.value.course.displays.textbookterm[1])
  
  return now.isAfter(startDate) && now.isBefore(endDate)
})

const isWithinCertificateIssueTerm = computed(() => {
  if (!detail.value) return false
  return commonUtil.isWithinCertificateIssueTerm(detail.value)
})

// 全講座一覧を取得する関数（申込キャンセル時用）
const getCourses = async () => {
  try {
    const res = await learningApiCourses.getCourses({params: {year: dayjs().year(), with: 'location'}})
    console.log('getCourses res', res.data)
    courseList.value = res.data
  } catch (err) {
    console.error('getCourses err', err)
  }
}

onMounted(async () => {
  window.scrollTo(0, 0)

  Promise.all([
    learningApiMasters.getMastersPaymentMethod(),
    learningApiUser.getUser(),
    learningApiCourses.getCourses({params: {year: dayjs().year(), with: 'location'}})
  ]).then((res) => {
    // 支払い方法取得
    console.log('getMastersPaymentMethod res', res[0].data)
    paymentMethod.value = res[0].data

    // ユーザー情報取得
    console.log('getUser res', res[1])
    setUserInfo(res[1].data)
    // flagsにnotModifiableがあれば編集不可
    if (res[1].data.flags && res[1].data.flags.includes('notModifiable')) {
      userModifiable.value = false
    }

    // 今年度の全講座一覧取得
    console.log('getCourses res', res[2].data)
    courseList.value = res[2].data
    getTrainee()
  }).catch((err) => {
    console.error('onMounted err', err)
  })
})

/* 退会処理 */
const withdrawal = () => {
  showWithdrawalModal.value = true
}

const cancelWithdrawal = () => {
  showWithdrawalModal.value = false
}

const doWithdrawal = () => {
  rootAppProvide.value.openContentsLoading()
  showWithdrawalModal.value = false
  learningApiUser.sendResign().then((res) => {
    console.log('deleteUser res', res)
    logout()
  }).catch((err) => {
    console.error('deleteUser err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

/* ログアウト */
const logout = () => {
  resetAuth()
  window.location.href = '/index.html'
}

/* iframeの高さ調整 */
window.addEventListener('load', function() {
  document.querySelectorAll('iframe.autoHeight').forEach(function(iframe) {
    var D = iframe.contentWindow.document
    var innerHeight = Math.max(
      D.body.scrollHeight, D.documentElement.scrollHeight,
      D.body.offsetHeight, D.documentElement.offsetHeight,
      D.body.clientHeight, D.documentElement.clientHeight
    )
    iframe.removeAttribute('height')
    iframe.style.height = innerHeight + 'px'
  })
})
</script>

<template>
  <div class="container">
    <!-- 内部HTML読み込み-->
    <iframe class="autoHeight" src="/api/v2/masters/user/includeHTML1"></iframe>

    <div class="topMessage">
      <p>{{ topMessage }}</p>
    </div>

    <!-- 申込み講座あり -->
    <div
      v-if="detail"
      class="detail"
    >
      <!-- 講座詳細（共通） -->
      <table class="detailTable">
        <tr>
          <th>講座名称</th>
          <td>{{ detail.course.displays && detail.course.displays.name ? detail.course.displays.name : '' }}</td>
        </tr>
        <tr>
          <th>講座タイプ</th>
          <td>{{ detail.course.type === 'online' ? (detail.course.besent ? 'オンデマンド＋DVD' : 'オンデマンド') : '会場' }}</td>
        </tr>
        <tr v-if="detail.course.type === 'online'">
          <th>DVD送付</th>
          <td>{{ detail.course.besent ? '有' : '無' }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場名</th>
          <td>{{ detail.course.location.displays.name }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場住所</th>
          <td>〒{{ detail.course.location.displays.address.zipcode }} {{ detail.course.location.displays.address.pref }} {{ detail.course.location.displays.address.city }} {{ detail.course.location.displays.address.street }} {{ detail.course.location.displays.address.building }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場地図</th>
          <td>{{ detail.course.location.latlong }}</td>
        </tr>
        <tr>
          <th>開催期間</th>
          <td>
            {{ dayjs(detail.course.term[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
            {{ dayjs(detail.course.term[1]).format('YYYY/MM/DD(dd) HH:mm') }}
          </td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>定員</th>
          <td>
            {{ detail.course.displays.capacity }}
          </td>
        </tr>
        <tr>
          <th>受講料</th>
          <td>
            {{ detail.course.price.toLocaleString() }}円（税込）
          </td>
        </tr>
        <tr>
          <th>支払い状況</th>
          <td>
            {{ detail.payment_status === -1 ? '未払い' : detail.payment_status === -2 ? '返金済' : '支払済' }}
            <template v-if="detail.payment_status === 1">
              （{{ detail.payment_by === 'GMOPay/KONBINI' ? 'コンビニ払い' : detail.payment_by === 'GMOPay/CREDIT' ? 'クレジットカード' : '銀行振込' }}）
            </template>
            <button
              v-if="detail.bill_issue"
              class="c-button c-button--small c-button--yellow"
              @click="confirmPDFDL('bill')"
            >
              請求書の発行
            </button>
            <button
              v-if="detail.receipt_issue"
              class="c-button c-button--small c-button--yellow"
              @click="confirmPDFDL('receipt')"
            >
              領収書の発行
            </button>
          </td>
        </tr>
        <tr>
          <th>受講状況</th>
          <td>
            {{ commonUtil.getTraineeStatus(detail).name }}
          </td>
        </tr>
        <tr v-if="detail.certificate">
          <th>修了証No</th>
          <td>
            {{ detail.certificate }}
          </td>
        </tr>
      </table>

      <!-- 受講前決済（未決済） オンデマンド・オフライン共通 -->
      <template v-if="traineeStatus.paymentStatus === -1">
        <!-- 団体管理者支払い待ち -->
        <div
          v-if="!detail.changeable"
          class="onlineContainer changeableFalse"
        >
          団体管理者の決済待ちです
        </div>

        <!-- 決済方法未選択 -->
        <div
          v-else-if="traineeStatus.paymentSelect === null"
          class="onlineContainer"
        >
          <p class="title">お支払い方法を選択してください</p>
          <template v-if="isPayable('GMOPay/BANK_TRANSFER_GMO_AOZORA')">
            <p>銀行振込</p>
            <div class="paymentByContainer">
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/BANK_TRANSFER_GMO_AOZORA')"
              >
                銀行振込※1
              </button>
            </div>
          </template>

          <template v-if="isPayable('GMOPay/KONBINI')">
            <p>コンビニ払い※2</p>
            <div class="paymentByContainer">
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/KONBINI', 'SEVEN_ELEVEN')"
              >
                セブンイレブン
              </button>
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/KONBINI', 'LAWSON')"
              >
                ローソン
              </button>
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/KONBINI', 'FAMILYMART')"
              >
                ファミリーマート
              </button>
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/KONBINI', 'MINISTOP')"
              >
                ミニストップ
              </button>
              <button
                class="c-button c-button--small c-button--yellow"
                @click="openPaySelectModal('GMOPay/KONBINI', 'SEICOMART')"
              >
                セイコーマート
              </button>
            </div>
          </template>

          <p>その他</p>
          <div class="paymentByContainer">
            <button
              v-if="isPayable('GMOPay/PAYEASY')"
              class="c-button c-button--small c-button--yellow"
              @click="openPaySelectModal('GMOPay/PAYEASY')"
            >
              ペイジー払い※3
            </button>
          </div>

          <ul class="paymentByAttention">
            <li>※1 受講料に加えて90円（税込）の事務手数料が必要となります。別途、ご利用の金融機関の振込手数料をご負担ください。</li>
            <li>※2 受講料に加えて310円（税込）の事務手数料が必要となります。</li>
            <li>※3 受講料に加えて310円（税込）の事務手数料が必要となります。</li>
          </ul>
        </div>

        <!-- 決済方法選択済、未払 -->
        <div
          v-else
          class="onlineContainer p0"
        >
          <p class="title">選択した方法でまだお支払いが完了していません。</p>
          <div class="btnContainer">
            <button
              class="c-button c-button--small c-button--yellow"
              @click="toPayDetail()"
            >
              お支払い方法を確認
            </button>
          </div>
        </div>
      </template>

      <!-- オンデマンド講座 ここから↓↓↓↓↓↓ -->

      <!-- 支払済、期間中受講前 -->
      <template v-if="traineeStatus.paymentStatus === 1 && detail.course.type === 'online' && detail.course.being">
        <!-- 開始前、受講中 -->
        <div
          v-if="!detail.attendance && !detail.end_at"
          class="onlineContainer"
        >
          <!-- 開始前 -->
          <template v-if="onlineMethod === null">
            <p class="title">オンデマンド講座を開始できます。視聴方法を選択してください</p>

            <!-- 視聴方法選択ボタン -->
            <button
              class="c-button c-button--small c-button--yellow"
              @click="startOnline('online')"
            >
              オンデマンドで視聴
            </button>

            <button
              class="c-button c-button--small c-button--yellow"
              @click="startOnline('dvd')"
            >
              DVDで視聴
            </button>
          </template>

          
          <!-- PDFダウンロード（PDFがあり期間内の場合） -->
          <TextbookDownload
            v-if="onlineMethod === 'dvd' || onlineMethod === 'online'"
            :detail="detail"
          />

          <!-- オンデマンドでDVDかオンデマンド視聴を選択 -->
          <DVDorMovie
            v-if="onlineMethod === 'dvd' || onlineMethod === 'online'"
            :online-method="onlineMethod"
            :detail="detail"
          />

          <!-- キーワード入力（DVD、オンデマンド共通） -->
          <template v-if="onlineMethod && keywords.length > 0">
            <!-- <hr /> -->
            <p>動画に出てきたキーワードを全て入力し、「終了する」ボタンを押してください</p>
            <div class="keywordContainer">
              <div
                v-for="(item, index) in keywords"
                :key="'keyword' + index"
              >
                <p>{{ item.title }}</p>
                <input v-model="keywords[index].keyword" type="text" placeholder="キーワードを入力"/>
              </div>
            </div>
            <div
              v-if="errorOnlineKeyword"
              class="attention"
            >
              <p>{{ errorOnlineKeyword }}</p>
            </div>
            <button
              class="c-button c-button--small c-button--yellow"
              @click="finishOnline"
              :disabled="keywords.some((item) => item.keyword === '') ? 'disabled' : null"
            >
              終了する
            </button>
          </template>
        </div>

        <!-- オンデマンド講座 受講後 -->
        <template v-else>
          <!-- 受講後もPDFダウンロード可（PDFがあり期間内の場合） -->
          <TextbookDownload
            :detail="detail"
            :finished="true"
          />

          <!-- 受講後も開催期間中ならオンデマンドで閲覧可 -->
          <DVDorMovie
            :online-method="'online'"
            :detail="detail"
            :finished="true"
          />

          <!-- アンケート未回答 -->
          <div
            v-if="detail.course.questionnaire && !detail.questionnaire"
            class="onlineContainer"
          >
            <p class="title">受講お疲れ様でした。アンケートのご協力をお願いします。</p>
            <div class="questionnaireContainer">
              <div
                v-for="(item, index) in detail.course.questionnaire"
                :key="'question' + index"
                class="q_container"
              >
                <pre class="q_title">{{ item.question }}</pre>

                <!-- ラジオボタン -->
                <tepmlate v-if="item.type === 'radio'">
                  <div
                    v-for="(answer, answerIndex) in item.answers"
                    :key="index + answerIndex"
                    class="q_radio"
                  >
                    <input
                      :id="`item-${index}-${answerIndex}`"
                      v-model="questionnaire[index]"
                      type="radio"
                      :value="answer"
                    >
                    <label
                      :for="`item-${index}-${answerIndex}`"
                    >
                      {{ answer }}
                    </label>
                  </div>
                </tepmlate>

                <!-- チェックボックス -->
                <tepmlate v-if="item.type === 'checkbox'">
                  <div
                    v-for="(answer, answerIndex) in item.answers"
                    :key="index + answerIndex"
                    class="q_checkbox"
                  >
                    <label>
                      <input
                        name="item"
                        type="checkbox"
                        :value="answer"
                        @change="updateQuestionnaire(index, answer)"
                      >
                      {{ answer }}
                    </label>
                  </div>
                </tepmlate>

                <!-- セレクトボックス -->
                <tepmlate v-if="item.type === 'select'">
                  <select
                    v-model="questionnaire[index]"
                    class="q_select"
                  >
                    <option value="">
                      選択してください
                    </option>
                    <option
                      v-for="(answer, answerIndex) in item.answers"
                      :key="index + answerIndex"
                      :value="answer">
                      {{ answer }}
                    </option>
                  </select>
                </tepmlate>

                <!-- text -->
                <textarea
                  v-if="item.type === 'text'"
                  v-model="questionnaire[index]"
                  class="q_textarea"
                  @input="checkLengthQuestionnaireTextArea(index)"
                />
              </div>
              <button
                class="c-button c-button--small c-button--yellow"
                :disabled="!isPostableQuestionnaire"
                @click="postQuestionnaire"
              >
                回答を送信
              </button>
            </div>
          </div>
          <!-- アンケート回答済 -->
          <div
            v-if="detail.questionnaire"
            class="onlineContainer p0"
          >
            <p>
              アンケートに回答済です。
            </p>
          </div>
        </template>
      </template>

      <!-- オンデマンド講座 ここまで↑↑↑↑↑↑ -->

      <!-- 共通フッター -->
      <div class="footerBtnContainer">
        <button
          v-if="(trainable && detail.payment_status === -1) && detail.changeable"
          type="submit"
          class="c-button c-button--small c-button--red btnCancel"
          @click="showTraineeCancelModal = true"
        >
          申込キャンセル
        </button>

        <button
          v-if="detail.payment_status === 1 && detail.course.type === 'real'"
          class="c-button c-button--medium c-button--yellow"
          @click="openTraineeCard"
        >
          受講票を表示
        </button>

        <button
          v-if="detail.certificate_issue"
          class="c-button c-button--medium c-button--yellow"
          :disabled="!isWithinCertificateIssueTerm"
          @click="confirmPDFDL('cert')"
        >
          <span v-if="isWithinCertificateIssueTerm">
            修了証の発行
          </span>
          <span v-else>
            修了証の発行期間外
          </span>
        </button>
      </div>
    </div>

    <!-- 申込み講座なし、これから選択 -->
    <div v-else>
      <h2>{{ dayjs().year() }}年度講座</h2>

      <div class="scroll-box">
        <table
          v-if="onlineList.length > 0"
          class="courseListTable onlineList"
        >
          <thead>
            <tr>
              <th>講座名称</th>
              <th>DVD送付</th>
              <th>申込期間<br>開催期間</th>
              <th>受付中</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in onlineList"
              :key="'course' + index"
              @click="router.push({name: 'CourseDetail', params: { courseId: item.id }})"
            >
              <td>
                {{ item.displays && item.displays.name ? item.displays.name : '' }}
              </td>
              <td>
                {{ item.besent ? '有' : '無' }}
              </td>
              <td>
                {{ dayjs(item.entryterm[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
                {{ dayjs(item.entryterm[1]).format('YYYY/MM/DD(dd) HH:mm') }}
                <br>
                {{ dayjs(item.term[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
                {{ dayjs(item.term[1]).format('YYYY/MM/DD(dd) HH:mm') }}
              </td>
              <td>
                {{ item.entryable ? '◯' : '✕' }}
              </td>
              <td>
                <button @click="router.push({name: 'CourseDetail', params: { courseId: item.id }})">
                  申込
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else>
          <p>申込可能なオンデマンド講座はありません</p>
        </div>
      </div>
    </div>

    <!-- ガイドテーブル -->
    <div class="guide">
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--blue"
            @click="router.push({name: 'History'})"
          >
            受講票・受講履歴
          </button>
        </div>
        <div class="guide__td">
          受講申込済みの方は、ここから受講票が確認できます。<br>
          過去6年より以前の受講履歴につきましては受付・管理センターにお問合せください。<br>
          【キャンセルについて】<br>
          決済完了後の返金はできません。
        </div>
      </div>
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--blue"
            @click="router.push({path: '/user/account/input'})"
          >
            登録情報の確認・変更
          </button>
        </div>
        <div class="guide__td">
          詳細登録情報（住所・電話番号など）を行う場合はこちらから行ってください。尚、パスワード・メールアドレスの変更はこちらではできません。<br>
          団体でご登録された受講者様はここからは変更できません。登録情報に変更のある場合は、団体管理者様にご連絡ください。
        </div>
      </div>
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--blue"
            @click="router.push({path: '/user/mail'})"
          >
            メールアドレスの変更
          </button>
        </div>
        <div class="guide__td">
          メールアドレスを変更します。変更後は新しいメールアドレスに確認メールが送信されます。
        </div>
      </div>
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--blue"
            @click="router.push({path: '/user/password/edit'})"
          >
            パスワードの変更
          </button>
        </div>
        <div class="guide__td">
          パスワードを変更します。変更後は再度ログインが必要です。
        </div>
      </div>
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--red"
            :disabled="!userModifiable"
            @click="withdrawal"
          >
            登録解除
          </button>
        </div>
        <div class="guide__td">
          「退会」をご希望の⽅は、こちらから⼿続きができます。
          <span
            v-if="!userModifiable"
            class="attention"
          >
            <br>※退会手続きができないアカウントです。
          </span>
        </div>
      </div>
      <div class="guide__tr">
        <div class="guide__th">
          <button
            class="c-button c-button--medium c-button--black"
            @click="logout"
          >
            ログアウト
          </button>
        </div>
        <div class="guide__td">
          ログアウトします。
        </div>
      </div>
    </div>

    <Modal :show="showPaySelectModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            支払い方法は<br>
            『{{ tempPaySelect.name }}』<br>
            でよろしいですか？<br><br>
            はいを押した後のお支払い方法変更は<br>
            申込みをキャンセルする必要があります。
          </h2>
          <div class="l-modal__buttons l-modal__buttons--twin">
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
              type="button"
              @click="showPaySelectModal = false"
            >
              いいえ
            </button>
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
              type="button"
              @click="toPay"
            >
              はい
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showOnlineCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            オンデマンド講座が終了しました。<br>
            お疲れ様でした。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="hideOnlineCompleteModal"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showQuestionnaireCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            アンケートを送信します。<br>
            ご協力ありがとうございました。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="showQuestionnaireCompleteModal = false"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showTraineeCancelModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            申込みをキャンセルしますか？
          </h2>
          <div class="l-modal__buttons l-modal__buttons--twin">
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
              type="button"
              @click="showTraineeCancelModal = false"
            >
              いいえ
            </button>
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
              type="button"
              @click="traineeCancel"
            >
              はい
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showCancelCompleteModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            申込みをキャンセルしました。
          </h2>
          <div class="l-modal__buttons">
            <button
              class="c-modalBtn c-modalBtn--single c-modalBtn--gray"
              type="button"
              @click="showCancelCompleteModal = false"
            >
              閉じる
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showConfirmPDFModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            一度発行した場合内容の変更はできません。<br>
            登録内容を確認してください。<br>
            ※以前ダウンロードしている場合は<br>
            同様の内容がダウンロードされます。
          </h2>
          <div class="l-modal__buttons l-modal__buttons--twin">
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
              type="button"
              @click="cancelPDFDL"
            >
              いいえ
            </button>
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
              type="button"
              :disabled="isPdfDownloading"
              @click="PDFModalAgree"
            >
              はい
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <Modal :show="showWithdrawalModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            「登録解除」に対する確認ページ
          </h2>
          <div class="l-modal__text">
            <p>「登録解除」ご希望の⽅へ注意事項</p>
            <ul>
              <li>・「登録解除」後はログインができなくなります。</li>
              <li>・ログインができなくなるため、マイページを閲覧できません。</li>
              <li>・マイページを閲覧できないので、ご⾃分の過去の受講履歴は確認できなくなります。</li>
              <li>・受講申込みの後、受講料⽀払済みで未受講の状況で「登録解除」した場合、<br>受講料の返⾦はできません。</li>
            </ul>
            <span>上記内容をご了承ください。</span>
          </div>
          <div class="l-modal__buttons l-modal__buttons--twin">
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
              type="button"
              @click="cancelWithdrawal"
            >
              閉じる
            </button>
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
              type="button"
              @click="doWithdrawal"
            >
              登録解除
            </button>
          </div>
        </div>
      </template>
    </Modal>

    <!-- 追加：オンデマンド開始確認モーダル -->
    <Modal :show="showOnlineStartModal">
      <template #body>
        <h2 class="l-modal__ttl">
          同意確認事項（教育目的での使用に関する誓約を含む）
        </h2>
        <div class="l-modal__text">
          <p>本研修に参加するにあたり、以下の事項に同意・誓約いたします。</p>
          <ol>
            <li>1. 本研修で提供される資料、内容、ノウハウ、映像等は、すべて教育目的に限定して使用します。</li>
            <li>2. 営利目的、転売、二次配布、SNSや第三者への無断共有・流用等は一切行いません。</li>
            <li>3. 提供された情報・コンテンツを、事前の書面による許可なく複製・転載・商用利用しません。</li>
          </ol>
          <ul>
            <li>・上記の同意事項を遵守することを、修了証発行の条件とします</li>
            <li>・運営側が同意違反を確認した場合、事後であっても修了証の取り消し・無効措置を行う場合があります。</li>
          </ul>
          <!-- 同意のチェックボックス -->
          <div class="checkbox-container">
            <input
              type="checkbox"
              id="agree"
              v-model="agree"
            >
            <label for="agree">
              本同意事項に違反した場合、修了証の発行が無効となる可能性があることを
              理解し、了承します。
            </label>
          </div>
        </div>
        <div class="l-modal__buttons l-modal__buttons--twin">
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
            type="button"
            @click="showOnlineStartModal = false"
          >
            キャンセル
          </button>
          <button
            class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
            type="button"
            @click="confirmOnlineStart"
          >
            同意して視聴開始
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.container {

  iframe {
    margin-top: 40px;
    width: 100%;
    height: 100px;
    border: none;
  }

  .topMessage {
    margin: 20px 0;
    padding: 20px;
    border: 3px solid #F00;

    p {
      font-size: 20px;
      text-align: center;
      color: #F00;
    }
  }

  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }

  .detail {
    width: 100%;

    table.detailTable {
      width: 100%;
      border-top: 1px solid #ddd;
      border-right: 1px solid #ddd;

      tr:hover {
        background: #f9f9f9;
      }

      th {
        width: 200px;
        height: 100%;
        padding: 8px 15px 10px;
        background: #eaeaea;
        border-bottom: 1px solid #ddd;
        font-size: 16px;
        text-align: right;
      }

      td {
        width: calc(100% - 200px);
        padding: 8px 15px 10px;
        border-bottom: 1px solid #ddd;
        font-size: 16px;
        word-break: break-word;

        button {
          display: inline-block;
          width: auto;
          padding: 3px 8px;
          vertical-align: 1px;
          margin: 0 10px 0 0;
        }
      }
    }
  }

  .onlineContainer {
    width: auto;
    padding: 0 0 10px;
    margin: 20px auto;
    justify-content: center;
    border: 1px solid #ddd;

    p {
      display: block;
      margin: 20px 10px;
      text-align: center;
      font-size: 16px;

      &.title {
        padding: 14px;
        background: #00008b;
        color: #FFF;
        font-size: 18px;
        margin: 0; 
      }
    }

    &.changeableFalse {
      padding-top: 10px;
      text-align: center;
      font-size: 18px;
      color: #F00;
      border: none;
    }

    .paymentByContainer {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;

      button {
        display: inline-block;
        width: auto;
        padding: 5px 15px;
        margin: 0 10px 10px 0;
      }
    }

    .paymentByAttention {
      margin: 20px 20px 10px;
      font-size: 14px;

      li {
        text-indent: -24px;
        padding-left: 24px;
        margin: 0;
      }
    }

    .iframeContainer {
      width: auto;
      margin: 5px auto 30px;
      text-align: center;
    }

    &.p0 {
      padding: 0;
      
      .btnContainer {
        text-align: center;

        button {
          display: inline-block;
          width: auto;
          padding: 5px 8px;
          margin: 20px 10px;
        }
      }
    }

    /* キーワード入力 */
    .keywordContainer {
      margin: 0 20px;
      
      p {
        padding: 8px 0 0;
        margin: 0;
        font-size: 14px;
        text-align: left;
      }

      input {
        display: block;
        margin: 4px auto 10px;
        width: 100%;
        padding: 8px;
        text-align: left;
        font-size: 16px;
        border: 1px solid #ddd;
      }
    }

    /* アンケート入力 */
    .q_container {
      padding: 0 15px;
      margin: 0 auto;
      
      .q_title {
        max-width: 100%;
        white-space: pre-wrap;
        word-wrap: break-word;
        padding: 8px 0 0;
        margin: 18px 0 0;
        font-size: 16px;
        font-weight: bold;
        text-align: left;
      }

      .q_radio {
        display: block;
        margin: 4px 16px 2px 0;
        font-size: 16px;

        input {
          vertical-align: -1px;
          appearance: auto;
        }
      }

      .q_checkbox {
        display: inline-block;
        margin: 4px 16px 2px 0;
        font-size: 16px;

        input {
          vertical-align: -1px;
          appearance: auto;
        }
      }

      .q_select {
        appearance: auto;
        outline: none;
        max-width: 100%;
        border: 1px solid #333;
        border-radius: 4px;
        font-size: 16px;
        margin-top: 8px;
        padding: 4px 10px;
        cursor: pointer;
      }

      .q_textarea {
        display: block;
        margin: 4px auto 10px;
        width: 100%;
        height: 100px;
        padding: 8px;
        border-radius: 4px;
        font-size: 16px;
        border: 1px solid #333;
      }
    }

    button {
      display: block;
      width: 300px;
      padding: 8px;
      margin: 20px auto 10px;
      font-size: 16px;
      text-align: center;
      height: auto;

      span {
        display: block;
        font-size: 13px;
      }
    }
  }

  // 今年度の講座一覧
  h3 {
    padding: 5px 10px;
    margin: 0 0 10px;
    border-left: 6px solid #00008b;
    font-size: 17.6px;
    font-weight: bold;
  }

  .courseListTable {
    width: 100%;
    min-width: 540px;

    thead {
      background: #eaeaea;
      border-bottom: 1px solid #ddd;

      th {
        border: none;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.4;
        padding: 6px 15px;
        text-align: left;
      }
    }
    tbody {
      tr:last-child td {
        border-bottom: 1px solid #ddd;
      }
      td {
        border-top: 1px solid #ddd;
        font-size: 16px;
        line-height: 1.3;
        padding: 12px 15px;
        vertical-align: middle;
        word-break: break-word;

        button {
          display: inline-block;
          width: auto;
          padding: 5px 15px;
          margin-left: 10px;
          background: #ffbf00;
          color: #fff;
          text-align: center;
          font-size: 1.4rem;
          color: #000;
          border-radius: 8px;
          text-decoration: none;
          vertical-align: middle;
        }
      }
    }

    &.onlineList {
      thead th {
        &:nth-child(1) {
          width: 230px;
        }
        &:nth-child(2) {
          width: 50px;
        }
        &:nth-child(3) {
          width: 300px;
        }
        &:nth-child(4) {
          width: 60px;
        }
        &:nth-child(5) {
          width: 70px;
        }
      }
    }

    &.realList {
      thead th {
        &:nth-child(1) {
          width: 80px;
        }
        &:nth-child(2) {
          width: 100px;
        }
        &:nth-child(3) {
          width: 300px;
        }
        &:nth-child(4) {
          width: 60px;
        }
        &:nth-child(5) {
          width: 100px;
        }
      }
    }
  }

  /* フッターボタン */
  .footerBtnContainer {
    position: relative;
    display: flex;
    width: auto;
    height: 40px;
    margin: 20px auto;
    justify-content: center;

    button {
      width: 200px;

      &:nth-child(2) {
        margin-left: 20px;
      }
    }

    .btnCancel {
      position: absolute;
      right: 0;
      top: 0;
      font-size: 1.6rem;
    }
  }
}

/* ガイドテーブル */
.guide {
  display: table;
  border: 3px solid #00008b;
  margin-top: 60px;

  &__tr {
    display: table-row;
  }

  &__th, &__td {
    display: table-cell;
    padding: 20px;
    font-size: 15px;
    vertical-align: middle;
  }

  &__th {
    width: 300px;

    button {
      width: 100%;
      padding: 5px 15px;
      font-size: 1.6rem;
    }
  }
}

@media (max-width: 576px) {
  .container .detail table.detailTable {
    th {
      width: auto;

      &:nth-child(1) {
        width: 110px;
      }
    }
  }

  .guide {
    display: block;
    padding: 5px 15px;

    &__tr {
      display: block;
      border-bottom: 1px solid #CCC;

      &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }
    }

    &__th {
      display: block;
      padding-top: 20px;
      width: 100%;

      button {
        width: 200px;
        margin: 0 auto;
      }
    }

    &__td {
      display: block;
      padding: 0 0 15px;
      width: 100%;
    }
  }
}

.attention {
  color: red;
  font-size: 14px;
}
</style>
